import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'understanding-redactions-in-your-care-records';

export const UnderstandingRedactions = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        It is common for records to arrive with sections blacked out — a name, a paragraph,
        sometimes whole pages. Seeing your own file redacted can feel like being shut out of
        your own history a second time. Sometimes a redaction is lawful. Often, especially in
        council social care files, it isn&apos;t — redaction is applied far more heavily, and far
        more automatically, than the law actually allows. This guide explains the real rules, so
        you can tell which redactions hold up and which don&apos;t, and what to do about the ones
        that don&apos;t.
      </p>
    </GuideSection>

    <GuideSection eyebrow="The basic rule" title="Every redaction needs its own reason">
      <p>
        An organisation cannot redact your file as a matter of policy, or because a record
        "might be sensitive." Each redaction has to be justified individually, against one of a
        specific, limited set of legal exemptions. If asked, an organisation should be able to
        tell you which exemption applies to a specific redaction, not just that "some
        information has been removed."
      </p>
    </GuideSection>

    <GuideSection eyebrow="What's usually lawful" title="Common, lawful reasons for redaction">
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">Someone else&apos;s personal data.</strong> Under Schedule
            2, paragraph 16 to the{' '}
            <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/schedule/2/paragraph/16">
              Data Protection Act 2018
            </SourceLink>
            , an organisation doesn&apos;t have to disclose information that would identify another
            individual, unless that person consents or it&apos;s reasonable to disclose it without
            their consent. This is the most common reason for redaction in care files, since so
            many records mention other children, carers or relatives.
          </>,
          <>
            <strong className="text-ink">Legal professional privilege.</strong> Under{' '}
            <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/schedule/2/paragraph/19">
              Schedule 2, paragraph 19
            </SourceLink>
            , legal advice sought by the organisation about you — for example, advice from their
            own lawyers ahead of care proceedings — can be withheld.
          </>,
          <>
            <strong className="text-ink">Health, social work and education-specific
            exemptions.</strong>{' '}
            <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/schedule/3">
              Schedule 3
            </SourceLink>{' '}
            sets out narrow exemptions for these categories of record — most notably, health
            information can be withheld where disclosing it would be likely to cause serious harm
            to your or someone else&apos;s physical or mental health. This is meant to be applied
            carefully, generally with input from an appropriate health professional, not as a
            default.
          </>,
          <>
            <strong className="text-ink">An ongoing investigation or crime-prevention
            purpose.</strong> Particularly relevant to police records — information can be
            withheld where disclosing it would be likely to prejudice the prevention or detection
            of crime, or an ongoing investigation.
          </>,
        ]}
      />
    </GuideSection>

    <GuideNote>
      <p>
        <strong>Three patterns of over-redaction turn up constantly in council files. None of
        them are lawful, and all three are worth challenging every time you see them.</strong>
      </p>
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">Blacking out every social worker or professional&apos;s
            name.</strong> A professional named in a document they wrote or were named in, acting
            in their professional capacity, is not "someone else&apos;s personal data" in the way a
            private individual is — the ICO is explicit that a member of staff&apos;s information is{' '}
            <strong className="text-ink">more</strong> likely to need disclosing, not less, when
            the person asking already knows them well through direct dealings, which describes
            almost every social worker, teacher or nurse named in a care file. Redacting every
            professional name as standard practice is not applying an exemption — it is refusing
            to follow the law.
          </>,
          <>
            <strong className="text-ink">Withholding a whole report by claiming it "isn&apos;t your
            personal data."</strong> A social worker&apos;s observations, assessment or opinion about
            you are exactly that — your personal data — regardless of who wrote them or why. The
            ICO&apos;s own test is whether the content is "obviously about" an individual, and a
            report someone wrote observing and assessing you plainly is. This excuse is sometimes
            used to withhold entire assessments rather than actually applying an exemption to
            them.
          </>,
          <>
            <strong className="text-ink">Withholding a whole report because it mentions a
            sibling.</strong> Mentioning someone else doesn&apos;t make a document theirs — it only
            ever justifies withholding the specific parts that identify them, not the rest of the
            report, and even that is subject to the same consent-or-reasonableness balancing test
            as any third-party information. The ICO explicitly says the importance of the
            information to you has to be weighed against the sibling&apos;s confidentiality, and
            since you and a sibling raised together usually already know a great deal about each
            other and share the same care history, that balance very often comes down in favour
            of disclosure, not blanket refusal.
          </>,
        ]}
      />
    </GuideNote>

    <GuideSection eyebrow="A practical route around it" title="The same report often exists, unredacted, somewhere else">
      <p>
        Social care files are not written in isolation. A referral, an assessment, a review
        report or a set of minutes is routinely copied or summarised to other organisations
        involved with you at the time — your GP, your school, a health visitor, a fostering
        agency, another team within the same council, sometimes the police. Each of those
        organisations holds its own copy, as a separate data controller with its own separate
        duty to respond to your SAR.
      </p>
      <p>
        That matters, because a redaction one organisation applies to its copy has no bearing on
        what another organisation is entitled to withhold from its own copy of the same
        document. If a council blacks out a paragraph, that exact paragraph may exist, unredacted
        or redacted differently, in the version your school or GP received and filed. Requesting
        from every organisation that was likely involved — not just the obvious one — is often
        the single most effective way to see what was taken out, without ever needing to win an
        argument about exemptions.
      </p>
      <GuideChecklist
        items={[
          'A report shared with your school or GP often lands in their file exactly as written, since neither has a reason to redact information about their own patient or pupil.',
          "Minutes from a meeting several organisations attended — a review, a professionals' meeting, a strategy discussion — may be filed by each attendee separately, with different redactions, or none.",
          'A letter between two organisations about you is, by definition, held twice — once by whoever sent it, once by whoever received it.',
        ]}
      />
      <p>
        See our guides to requesting from{' '}
        <Link to="/guides/how-to-request-your-care-records-from-the-council" className="text-brand underline hover:opacity-90">
          the council
        </Link>
        ,{' '}
        <Link to="/guides/how-to-request-your-school-records" className="text-brand underline hover:opacity-90">
          schools
        </Link>{' '}
        and{' '}
        <Link to="/guides/how-to-request-your-medical-records" className="text-brand underline hover:opacity-90">
          GPs and hospitals
        </Link>{' '}
        for where else to ask.
      </p>
    </GuideSection>

    <GuideCTA
      heading="We compare every copy automatically"
      body="When more than one organisation sends you a copy of the same document, Precious Data lines them up and checks each blacked-out passage against the others — showing you what was probably removed, which organisation still has it unredacted, and whether it's worth challenging."
    />

    <GuideSection eyebrow="What to do" title="Challenging a redaction you think is wrong">
      <GuideChecklist
        items={[
          'Ask which exemption the redaction was made under. You are entitled to a proper answer, not a shrug.',
          'Ask for a redaction to be reviewed if it doesn\'t make sense — for instance, if a professional\'s name has been removed, or if a whole paragraph is missing where only one sentence looks sensitive.',
          "If it's a name being withheld because it identifies someone else, ask whether that person's consent has been sought, or why it was judged unreasonable to disclose it without consent — the organisation is meant to weigh this, not redact by default.",
          <>
            If you&apos;re still not satisfied, you can complain to the organisation directly, and
            then escalate to the Information Commissioner&apos;s Office if the response still
            doesn&apos;t hold up — see our guide on{' '}
            <Link
              to="/guides/what-to-do-if-an-organisation-misses-the-deadline"
              className="text-brand underline hover:opacity-90"
            >
              what to do if an organisation misses a deadline or won&apos;t engage
            </Link>{' '}
            for how that process works.
          </>,
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="redactions-faq">
        <GuideFaqItem
          q="Can they redact the reasons I was taken into care?"
          a="Generally no — the reasoning behind decisions made about your life is your personal data, even where it references other people's actions or circumstances. Organisations sometimes over-redact this kind of material; it's worth pushing back and asking for the specific exemption relied on."
        />
        <GuideFaqItem
          q="A social worker's name is blacked out. Is that allowed?"
          a="Usually not, and it's worth challenging every time. A professional's name, written in their professional capacity, is not treated the same as a private individual's — the ICO says disclosure is more likely to be required, not less, when you already know that professional through direct dealings, which is exactly the situation with almost every social worker named in your own file. Ask the organisation to justify it specifically rather than accepting a blanket redaction."
        />
        <GuideFaqItem
          q="What if whole pages are missing, not just redacted lines?"
          a="Ask directly whether pages have been withheld entirely, and under which exemption, versus simply not included by mistake. It's a fair, specific question, and the organisation should be able to answer it."
        />
        <GuideFaqItem
          q="They said a whole report 'isn't my personal data' and withheld it. Is that right?"
          a="Almost certainly not, if it's a social worker's or other professional's observations or assessment of you. The ICO's own test for whether something is your personal data is whether the content is obviously about you — an assessment someone wrote observing and evaluating you clearly is, regardless of who wrote it. This claim is sometimes used to withhold a whole document rather than properly applying (and justifying) a specific exemption to it."
        />
        <GuideFaqItem
          q="A whole report was withheld because it mentions my sibling. Is that allowed?"
          a="No — mentioning a sibling only ever justifies removing the specific parts that identify them, not the entire document, and even then the organisation has to weigh how important the information is to you against your sibling's confidentiality, not refuse automatically. Since you and a sibling you grew up with usually already know a great deal about each other, that balance very often favours giving you the report, not withholding it."
        />
        <GuideFaqItem
          q="Does Precious Data detect redactions for me?"
          a="Yes. We flag likely redactions and gaps automatically, and where more than one organisation sends you a copy of the same document, we compare them and reconstruct what's missing — showing you what was probably removed, which organisation's copy still has it, and whether it's worth challenging."
        />
        <GuideFaqItem
          q="What if the problem isn't a redaction, but something written that's wrong or unkind?"
          a={
            <>
              That's a different situation, with its own route — see our guide on{' '}
              <Link
                to="/guides/what-to-do-if-your-records-are-wrong-or-unkind"
                className="text-brand underline hover:opacity-90"
              >
                what to do if something in your records is wrong, or was written unkindly
              </Link>
              .
            </>
          }
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The exemptions themselves are set out in{' '}
        <SourceLink href="https://www.legislation.gov.uk/ukpga/2018/12/contents">
          Schedules 2 and 3 to the Data Protection Act 2018
        </SourceLink>
        . The ICO&apos;s{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/right-of-access/">
          right of access guidance
        </SourceLink>{' '}
        covers how organisations are expected to apply them in practice, including that
        redactions must be considered individually, not applied as blanket policy. Its page on{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/right-of-access/when-can-an-exemption-apply-to-information-about-other-people-in-a-sar/">
          when an exemption can apply to information about other people
        </SourceLink>{' '}
        is the direct source for the points about professionals you already know well and
        weighing a sibling&apos;s confidentiality against how important the information is to you.
        Its guidance on{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/personal-information-what-is-it/what-is-personal-data/what-is-the-meaning-of-relates-to/">
          what "relates to" an individual means
        </SourceLink>{' '}
        is the source for why a report observing and assessing you is your personal data
        regardless of who wrote it.
      </p>
    </GuideSection>

    <GuideCTA
      heading="We flag over-redaction and fill in the gaps"
      body="Once your records come in through Precious Data, we flag likely-unlawful redactions automatically — including blanket-redacted professional names — and where another organisation's copy of the same document is unredacted, we piece it together and show you what was missing."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default UnderstandingRedactions;
